<template>
  <div class="grid gap-5">
    <!-- Loading state -->
    <div v-if="isLoading" class="flex justify-center py-10">
      <Loading />
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="flex flex-col items-center text-gray-500 py-10">
      <ExclamationTriangleIcon class="w-8 h-8 mb-2 text-amber-500" />
      <p class="text-sm">{{ $t('components.salesreport.server_error') }}</p>
    </div>

    <!-- No data — ghost card -->
    <ReportCard v-else-if="!overviewData"
      :title="$t('report.clients_new_vs_returning')"
      title-class="text-foreground/40">
      <div class="flex items-end justify-center gap-2 h-40 px-6 mb-5">
        <div class="ct-ghost-bar flex-1 rounded-t" style="height: 35%"></div>
        <div class="ct-ghost-bar ct-ghost-bar--accent flex-1 rounded-t" style="height: 60%"></div>
        <div class="ct-ghost-bar flex-1 rounded-t" style="height: 25%"></div>
        <div class="ct-ghost-bar ct-ghost-bar--accent flex-1 rounded-t" style="height: 80%"></div>
        <div class="ct-ghost-bar flex-1 rounded-t" style="height: 45%"></div>
        <div class="ct-ghost-bar ct-ghost-bar--accent flex-1 rounded-t" style="height: 55%"></div>
        <div class="ct-ghost-bar flex-1 rounded-t" style="height: 30%"></div>
      </div>
      <div class="text-center pb-2">
        <p class="text-[13px] font-semibold text-muted-foreground">
          {{ $t('data.unavailable') }}
        </p>
        <p class="text-[11px] text-muted-foreground/50 mt-1">
          {{ $t('data.unavailable_hint') }}
        </p>
      </div>
    </ReportCard>

    <template v-else>
      <!-- 1. KPI Overview -->
      <ClientsOverviewKPIs
        :response="overviewData"
        :center-id="centerId"
      />

      <!-- 2 + 3. New vs Returning + Acquisition side by side -->
      <div class="grid grid-cols-2 gap-5">
        <div>
          <div class="flex flex-col items-center mb-3">
            <SegmentedPicker
              v-model="stackedAgg"
              :groups="stackedPickerGroups"
            />
          </div>
          <ClientsNewVsReturningChart
            ref="stackedChartRef"
            :response="stackedResponse"
            :format-x-label="stackedFormatXLabel"
            :loading="stackedLoading"
          />
        </div>

        <div>
          <div class="flex flex-col items-center mb-3">
            <SegmentedPicker
              v-model="cumulativeAgg"
              :groups="cumulativePickerGroups"
            />
          </div>
          <ClientsAcquisitionChart
            ref="cumulativeChartRef"
            :response="cumulativeResponse"
            :seasonality-response="seasonalityResponse"
            :has-comparison="hasComparison"
            :format-x-label="cumulativeFormatXLabel"
            :loading="cumulativeLoading"
          />
        </div>
      </div>

      <!-- 4 + 5. Revenue by card + Breakdown side by side -->
      <div class="grid grid-cols-2 gap-5">
        <div>
          <div class="flex flex-col items-center mb-3">
            <SegmentedPicker
              v-model="cardRevenueAgg"
              :groups="cardRevenuePickerGroups"
            />
          </div>
          <ClientsRevenueByCardChart
            ref="cardRevenueChartRef"
            :response="cardRevenueResponse"
            :format-x-label="cardRevenueFormatXLabel"
            :loading="cardRevenueLoading"
            :center-id="centerId"
          />
        </div>

        <ClientsByCardBreakdown
          :response="overviewData"
          :center-id="centerId"
        />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { ExclamationTriangleIcon } from '@heroicons/vue/24/outline'
import { useReportContext } from '../../../composables/useReportContext'
import { useTimeAggregation } from '../../../composables/useTimeAggregation'
import { SalesApi } from '../../../api/SalesApi'
import type { SalesByClientsResponse } from '../../../responses'
import ReportCard from '../../ReportCard.vue'
import Loading from '../../Loading.vue'
import SegmentedPicker, { type PickerGroup } from '../../ui/SegmentedPicker.vue'
import ClientsOverviewKPIs from '../../report-sections/ClientsOverviewKPIs.vue'
import ClientsNewVsReturningChart from '../../report-sections/ClientsNewVsReturningChart.vue'
import ClientsAcquisitionChart from '../../report-sections/ClientsAcquisitionChart.vue'
import ClientsRevenueByCardChart from '../../report-sections/ClientsRevenueByCardChart.vue'
import ClientsByCardBreakdown from '../../report-sections/ClientsByCardBreakdown.vue'

const { t } = useI18n()
const { card, centerId } = useReportContext()

const isLoading = ref(false)
const error = ref(false)

// Overview data (KPI, by_card, concentration, anonymous) — independent of chart aggregation
const overviewData = ref<SalesByClientsResponse | null>(null)

// --- Date refs for useTimeAggregation ---
const startDate = computed(() => card.value?.date?.startDate?.toJSDate() ?? new Date())
const endDate = computed(() => card.value?.date?.endDate?.toJSDate() ?? new Date())

// --- Independent aggregation per chart ---
const {
  aggregation: stackedAgg,
  aggregationOptions: stackedAggOptions,
  formatXLabel: stackedFormatXLabel,
} = useTimeAggregation(startDate, endDate, { includeDistributions: true, distributions: ['weekday'] })

const {
  aggregation: cumulativeAgg,
  aggregationOptions: cumulativeAggOptions,
  formatXLabel: cumulativeFormatXLabel,
} = useTimeAggregation(startDate, endDate, { includeDistributions: false })

const {
  aggregation: cardRevenueAgg,
  aggregationOptions: cardRevenueAggOptions,
  formatXLabel: cardRevenueFormatXLabel,
} = useTimeAggregation(startDate, endDate, { includeDistributions: true })

function buildPickerGroups(options: typeof stackedAggOptions.value): PickerGroup[] {
  const chronological = options.filter(o => o.group === 'chronological')
  const distribution = options.filter(o => o.group === 'distribution')
  const groups: PickerGroup[] = []
  if (chronological.length) {
    groups.push({
      label: t('time_aggregation.group_chronological'),
      options: chronological.map(o => ({ value: o.value, label: o.label })),
    })
  }
  if (distribution.length) {
    groups.push({
      label: t('time_aggregation.group_distribution'),
      options: distribution.map(o => ({ value: o.value, label: o.label })),
    })
  }
  return groups
}

const stackedPickerGroups = computed(() => buildPickerGroups(stackedAggOptions.value))
const cumulativePickerGroups = computed(() => buildPickerGroups(cumulativeAggOptions.value))
const cardRevenuePickerGroups = computed(() => buildPickerGroups(cardRevenueAggOptions.value))

const hasComparison = computed(() => overviewData.value?.metadata.has_comparison === true)

// --- Per-chart data & loading ---
const stackedResponse = ref<SalesByClientsResponse | null>(null)
const stackedLoading = ref(false)

const cumulativeResponse = ref<SalesByClientsResponse | null>(null)
const cumulativeLoading = ref(false)

// N-1 year data specifically for projection seasonality
const seasonalityResponse = ref<SalesByClientsResponse | null>(null)

const cardRevenueResponse = ref<SalesByClientsResponse | null>(null)
const cardRevenueLoading = ref(false)

// --- Chart component refs ---
const stackedChartRef = ref<InstanceType<typeof ClientsNewVsReturningChart>>()
const cumulativeChartRef = ref<InstanceType<typeof ClientsAcquisitionChart>>()
const cardRevenueChartRef = ref<InstanceType<typeof ClientsRevenueByCardChart>>()

// --- Fetch helper ---

async function fetchForChart(interval: string): Promise<SalesByClientsResponse> {
  if (!card.value) throw new Error('No card')
  const deposit = card.value.dataType === 'deposit'
  return SalesApi.fetchSalesByClients(
    card.value.date.startDate, card.value.date.endDate,
    interval as any, card.value.dataOrigin, deposit, centerId,
    card.value.previousDate.startDate, card.value.previousDate.endDate
  )
}

async function fetchSeasonalityData(interval: string): Promise<SalesByClientsResponse> {
  if (!card.value) throw new Error('No card')
  const deposit = card.value.dataType === 'deposit'
  const n1YearStart = card.value.date.startDate.minus({ years: 1 })
  const n1YearEnd = card.value.date.endDate.minus({ years: 1 })
  return SalesApi.fetchSalesByClients(
    n1YearStart, n1YearEnd,
    interval as any, card.value.dataOrigin, deposit, centerId
  )
}

// --- Watchers ---

const restoring = ref(false)

// Stacked chart aggregation change
watch(stackedAgg, async (newAgg) => {
  if (!card.value || restoring.value) return
  stackedLoading.value = true
  try {
    stackedResponse.value = await fetchForChart(newAgg)
  } catch { /* silent */ }
  finally { stackedLoading.value = false }
  await nextTick()
  stackedChartRef.value?.refreshChart()
})

// Cumulative chart aggregation change
watch(cumulativeAgg, async (newAgg) => {
  if (!card.value || restoring.value) return
  cumulativeLoading.value = true
  try {
    const [cumulResp, seasonResp] = await Promise.all([
      fetchForChart(newAgg),
      fetchSeasonalityData(newAgg),
    ])
    cumulativeResponse.value = cumulResp
    seasonalityResponse.value = seasonResp
  } catch { /* silent */ }
  finally { cumulativeLoading.value = false }
  await nextTick()
  cumulativeChartRef.value?.refreshChart()
})

// Card revenue chart aggregation change
watch(cardRevenueAgg, async (newAgg) => {
  if (!card.value || restoring.value) return
  cardRevenueLoading.value = true
  try {
    cardRevenueResponse.value = await fetchForChart(newAgg)
  } catch { /* silent */ }
  finally { cardRevenueLoading.value = false }
  await nextTick()
  cardRevenueChartRef.value?.refreshChart()
})

// Restore from model cache or use pre-fetched data from ReportDesktopCard
const initRestoring = ref(false)
if (card.value?.clientsAggregation && card.value.clientsReport) {
  initRestoring.value = true
  restoring.value = true
  const cached = card.value.clientsReport
  overviewData.value = cached
  stackedResponse.value = cached
  cumulativeResponse.value = cached
  cardRevenueResponse.value = cached
  stackedAgg.value = card.value.clientsAggregation as typeof stackedAgg.value
  cumulativeAgg.value = card.value.clientsAggregation as typeof cumulativeAgg.value
  cardRevenueAgg.value = card.value.clientsAggregation as typeof cardRevenueAgg.value
  // Fetch N-1 year seasonality data for projection (non-blocking)
  fetchSeasonalityData(cumulativeAgg.value)
    .then(resp => { seasonalityResponse.value = resp; nextTick().then(() => cumulativeChartRef.value?.refreshChart()) })
    .catch(() => { seasonalityResponse.value = null })
  setTimeout(() => { restoring.value = false }, 0)
} else if (card.value?.clientsReport) {
  // Data pre-fetched by ReportDesktopCard, use directly
  const resp = card.value.clientsReport
  overviewData.value = resp
  stackedResponse.value = resp
  cumulativeResponse.value = resp
  cardRevenueResponse.value = resp
  // Fetch N-1 year seasonality data for projection (non-blocking)
  fetchSeasonalityData(cumulativeAgg.value)
    .then(resp => { seasonalityResponse.value = resp; nextTick().then(() => cumulativeChartRef.value?.refreshChart()) })
    .catch(() => { seasonalityResponse.value = null })
}

// Watch for data arriving from ReportDesktopCard background fetch
watch(() => card.value?.clientsReport, (newReport) => {
  if (newReport && !overviewData.value) {
    overviewData.value = newReport
    stackedResponse.value = newReport
    cumulativeResponse.value = newReport
    cardRevenueResponse.value = newReport
    // Fetch N-1 year seasonality data for projection (non-blocking)
    fetchSeasonalityData(cumulativeAgg.value)
      .then(resp => { seasonalityResponse.value = resp; nextTick().then(() => cumulativeChartRef.value?.refreshChart()) })
      .catch(() => { seasonalityResponse.value = null })
  }
})

// Save aggregation when leaving, but do NOT clear data (desktop keeps it cached)
onBeforeUnmount(() => {
  if (!card.value) return
  card.value.clientsAggregation = stackedAgg.value
})
</script>

<style scoped>
/* Ghost bar chart — empty state */
.ct-ghost-bar {
  background: rgba(156, 163, 175, 0.10);
  animation: ct-ghost-pulse 3s ease-in-out infinite;
}
.ct-ghost-bar--accent {
  background: rgba(6, 200, 255, 0.08);
  animation-delay: 0.4s;
}
.ct-ghost-bar:nth-child(3) { animation-delay: 0.8s; }
.ct-ghost-bar:nth-child(5) { animation-delay: 1.2s; }
.ct-ghost-bar:nth-child(7) { animation-delay: 1.6s; }

@keyframes ct-ghost-pulse {
  0%, 100% { opacity: 0.5; }
  50% { opacity: 1; }
}

:is(.dark *) .ct-ghost-bar {
  background: rgba(255, 255, 255, 0.04);
}
:is(.dark *) .ct-ghost-bar--accent {
  background: rgba(6, 200, 255, 0.06);
}
</style>
